// @flow

import React from "react";
import { localize } from "redux-i18n";
import config from "@config";
import { GSGrid, Icons, Pagination, SectionComponent, ConfirmationModal } from "@components";
import { Link } from "react-router-dom";

import type { NotificationContact } from "@modules/profile/types";
import { formatDate } from "@transformers/date";
import type { PaginationExtendedType } from "@types";
import { route } from "@routes/helper";
import { EMPLOYEE_REF_TYPES } from "@modules/shared/constants/notifications";
import { NotificationsAddModal } from "@modules/shared/components";
import { USER_TYPES } from "@modules/account/constants";

type Props = $Exact<{
  clientId: number,
  selectedEmployeeId: ?number,
  data: Array<NotificationContact>,
  pagination: PaginationExtendedType,
  allowEdit: boolean,
  notificationAuditRequiresRemoval: boolean,
  handlePaginationChange: (page: number) => void,
  toggleAddNotificationModal: (showModal: boolean) => void,
  showAddNotificationModal: boolean,
  notificationAuditRemoveAccessToRecord: ({
    clientId: number,
    individualNotificationId: number,
  }) => void,
  setSelectedEmployeeId: (id: number) => void,
  loadNotificationContacts: () => void,
  ...PropsFromLocalize,
}>;

type State = $Exact<{
  selectedContact: ?NotificationContact,
  showRemoveAccessConfirmationModal: boolean,
}>;

export class MyEmployeesList extends React.Component<Props, State> {
  state = {
    selectedContact: null,
    showRemoveAccessConfirmationModal: false,
  };

  handleShowAddNotificationModal = (notificationContact: NotificationContact) => () => {
    this.props.setSelectedEmployeeId(notificationContact.employee.id);
    this.props.toggleAddNotificationModal(true);
  };

  deleteNoteModal = (selectedContact?: NotificationContact) => () => {
    this.setState({
      selectedContact,
      showRemoveAccessConfirmationModal: !this.state.showRemoveAccessConfirmationModal,
    });
  };

  toggleRemoveAccessConfirmationModal = () => {
    this.setState({
      showRemoveAccessConfirmationModal: !this.state.showRemoveAccessConfirmationModal,
    });
  };

  confirmRemoveAccess = () => {
    if (this.state.selectedContact) {
      this.props.notificationAuditRemoveAccessToRecord({
        clientId: this.props.clientId,
        individualNotificationId: this.state.selectedContact.id,
      });
    }
    this.toggleRemoveAccessConfirmationModal();
  };

  get editPermission() {
    return [USER_TYPES.ADMIN, USER_TYPES.CLIENT_ADMIN, USER_TYPES.SERVICE_MANAGER].includes(
      this.props.userType,
    );
  }

  get columns() {
    let cols = [
      {
        key: "employeeReference",
        align: "left",
        name: this.props.t("EMPLOYEE_REF"),
        renderCell: ({ row }: { row: NotificationContact }) =>
          row.notificationType === "FULL_NOTIFICATION" ? (
            <Link
              to={route("REPORTING_EMPLOYEE_DETAILS_PATH", {
                clientId: this.props.clientId,
                employeeId: row.employee.id,
              })}
            >
              {row.employee.employeeRefPrimary}
            </Link>
          ) : (
            <div>{row.employee.employeeRefPrimary}</div>
          ),
      },
      {
        key: "firstName",
        name: this.props.t("FIRST_NAME"),
        align: "left",
        renderCell: ({ row }: Object) => <div className="ellipsis">{row.employee.firstName}</div>,
      },
      {
        key: "lastName",
        name: this.props.t("LAST_NAME"),
        align: "left",
        renderCell: ({ row }: Object) => <div className="ellipsis">{row.employee.lastName}</div>,
      },
      {
        key: "organisationalUnit",
        name: this.props.t("DEPARTMENT"),
        align: "left",
        renderCell: ({ row }: { row: NotificationContact }) =>
          (row.organisationalUnits || []).join(", "),
      },
      {
        key: "jobTitle",
        name: this.props.t("POSITION"),
        align: "left",
        renderCell: ({ row }: { row: NotificationContact }) => (row.jobTitles || []).join(", "),
      },
      {
        key: "notificationType",
        name: this.props.t("NOTIFICATION_TYPE"),
        align: "left",
        renderCell: ({ row }: { row: NotificationContact }) => this.props.t(row.notificationType),
      },
      {
        key: "createdAt",
        name: this.props.t("DATE_ADDED"),
        renderCell: ({ row }: { row: NotificationContact }) => formatDate(row.createdAt),
      },
      {
        key: "additionalContacts",
        name: this.props.t("ADDITIONAL_CONTACTS"),
      },
      {
        key: "view",
        width: "fit-content",
        align: "center",
        name: this.props.t("VIEW"),
        renderCell: ({ row }: { row: NotificationContact }) => {
          if (row.notificationType === "BASIC_NOTIFICATION" && !this.editPermission) {
            return null;
          }

          return (
            <div className="d-flex">
              <Link
                className="material-icons"
                aria-label={this.props.t("EMPLOYEE_DETAILS")}
                to={route("REPORTING_EMPLOYEES_AUDIT_EMPLOYEE_DETAILS", {
                  employeeId: row.employee.id,
                  clientId: this.props.clientId,
                })}
              >
                person_search
              </Link>
              {this.editPermission && (
                <div
                  className="ml-3 material-icons cursor-pointer"
                  onClick={this.handleShowAddNotificationModal(row)}
                >
                  person_add
                </div>
              )}
            </div>
          );
        },
      },
    ];
    if (this.props.notificationAuditRequiresRemoval) {
      cols.push({
        key: "remove",
        name: this.props.t("REMOVE"),
        align: "center",
        renderCell: ({ row }: { row: NotificationContact }) => {
          return (
            <span
              className="material-icons font-size-26 pointer"
              onClick={this.deleteNoteModal(row)}
            >
              delete
            </span>
          );
        },
      });
    }
    return cols;
  }

  get selectedContactName() {
    return (this.state.selectedContact || {}).employee;
  }

  render() {
    return (
      <SectionComponent>
        <div className="w-100  overflow-auto ">
          <GSGrid rows={this.props.data} columns={this.columns} />
        </div>
        <Pagination
          className="mx-auto mt-3 d-block"
          currentPage={this.props.pagination.pageNumber}
          totalPages={this.props.pagination.totalPages}
          onChange={this.props.handlePaginationChange}
        />
        {this.props.showAddNotificationModal && this.props.selectedEmployeeId && (
          <NotificationsAddModal
            clientId={this.props.clientId}
            employeeId={this.props.selectedEmployeeId}
            employeeRefType={EMPLOYEE_REF_TYPES.RECIPIENT}
            refreshAction={this.props.loadNotificationContacts}
          />
        )}

        {/* Confirm delete access modal */}
        {this.state.showRemoveAccessConfirmationModal && (
          <ConfirmationModal
            headerText={this.props.t("REMOVAL_CONFIRMATION")}
            bodyContent={this.props.t("CONFIRM_REMOVE_ACCESS", {
              key: this.selectedContactName.name,
            })}
            confirmBtnLabel={this.props.t("REMOVE_ACCESS")}
            onCancel={this.toggleRemoveAccessConfirmationModal}
            onConfirm={this.confirmRemoveAccess}
          />
        )}
      </SectionComponent>
    );
  }
}

export default localize()(MyEmployeesList);
